import {normalizeSearchText, findSearchTextRanges, splitSearchTextIntoSpans} from './song-search-text';
import {buildBm25DocumentProfile, buildBm25CorpusStats, scoreBm25} from './song-search-bm25';

export const prepareSearchDocument = source => {
  const lines = (source.content || '').split(/\r?\n/);
  const title = source.title || lines.find(line => /\|\|\s*$/.test(line))?.replace(/\s*\|\|\s*$/, '')
    || source.fileName.split('/').pop().replace(/\.txt$/i, '');
  const lyrics = source.lyrics ?? lines.filter(line => !/^\s*\[[^\]]*\]\s*$/.test(line) && !/\|\|\s*$/.test(line)).join('\n');
  const searchText = normalizeSearchText([title, lyrics, source.metadata, source.fileName].filter(Boolean).join(' '));
  return {...source, title, lyrics, searchText, profile: buildBm25DocumentProfile(searchText),
    spans: splitSearchTextIntoSpans(lyrics).map(span => ({...span, profile: buildBm25DocumentProfile(normalizeSearchText(span.text))}))};
};

const tokensOf = value => new Set(normalizeSearchText(value).split(' ').filter(word => word.length >= 2));
const compatible = (left, right) => {
  if (normalizeSearchText(left.title) !== normalizeSearchText(right.title)) return false;
  const a = normalizeSearchText(left.lyrics), b = normalizeSearchText(right.lyrics);
  if (a === b) return true;
  // Short refrains and reordered words do not establish that two songs are the same.
  if (a.split(' ').slice(0, 8).join(' ') !== b.split(' ').slice(0, 8).join(' ')) return false;
  const l = tokensOf(a), r = tokensOf(b);
  if (Math.min(l.size, r.size) < 8) return false;
  const intersection = [...l].filter(word => r.has(word)).length;
  return intersection / (l.size + r.size - intersection) >= 0.84;
};

export const buildSearchGroups = documents => {
  const groups = [];
  const exact = new Map();
  const byTitle = new Map();
  documents.slice().sort((a, b) => a.fileName.localeCompare(b.fileName, 'pl') || a.songNumber - b.songNumber).forEach(doc => {
    const key = `${normalizeSearchText(doc.title)}\u0000${normalizeSearchText(doc.lyrics)}`;
    let group = exact.get(key);
    const titleKey = normalizeSearchText(doc.title);
    const titleGroups = byTitle.get(titleKey) || [];
    if (!group) group = titleGroups.find(candidate => candidate.variants.every(variant => compatible(variant, doc)));
    if (!group) {
      group = {id: doc.id, variants: []}; groups.push(group); titleGroups.push(group); byTitle.set(titleKey, titleGroups);
    }
    group.variants.push(doc);
    exact.set(key, group);
  });
  return groups;
};

export const buildSearchCorpus = documents => buildBm25CorpusStats(
  documents.map(doc => doc.profile), documents.flatMap(doc => doc.spans.map(span => span.profile))
);

export const searchSnippet = (doc, terms, corpus) => {
  let best = null;
  for (const span of doc.spans) {
    const ranges = findSearchTextRanges(span.text, terms);
    if (!ranges.length) continue;
    const score = scoreBm25(span.profile, terms, corpus.fragments);
    if (!best || score > best.score || (score === best.score && ranges.length > best.ranges.length)) best = {...span, ranges, score};
  }
  const text = best?.text || doc.lyrics;
  const first = best?.ranges[0]?.start || 0;
  const start = Math.max(0, first - 60);
  const excerpt = text.slice(start, start + 180);
  return {snippet: excerpt, snippetRanges: best ? findSearchTextRanges(excerpt, terms) : [],
    titleRanges: findSearchTextRanges(doc.title, terms), sourceId: doc.id,
    sourceRevision: `${doc.title}\u0000${doc.lyrics}`};
};

export const rankSongGroups = ({query, groups, documents, corpus, fallbackHits = [], limit = 50}) => {
  const normalized = normalizeSearchText(query);
  if (!normalized) return [];
  const terms = normalized.split(' ');
  const direct = documents.filter(doc => terms.every(term => doc.searchText.includes(term)));
  const fallback = new Map(fallbackHits.map(hit => [hit.id, hit.score]));
  const candidates = direct.length ? new Set(direct.map(doc => doc.id)) : new Set(fallback.keys());
  const ranked = groups.flatMap(group => {
    const matches = group.variants.filter(doc => candidates.has(doc.id)).map(doc => ({
      doc, phrase: Number(doc.searchText.includes(normalized)),
      titlePhrase: Number(normalizeSearchText(doc.title).includes(normalized)),
      score: direct.length ? scoreBm25(doc.profile, terms, corpus.documents) : fallback.get(doc.id)
    })).sort((a, b) => b.phrase - a.phrase || b.titlePhrase - a.titlePhrase || b.score - a.score || a.doc.id.localeCompare(b.doc.id));
    const best = matches[0];
    return best ? [{group, ...best}] : [];
  }).sort((a, b) => b.phrase - a.phrase || b.titlePhrase - a.titlePhrase || b.score - a.score || a.group.id.localeCompare(b.group.id));
  return ranked.slice(0, limit).map(({group, doc, score}) => ({...doc, id: group.id, matchedVariantId: doc.id,
    variants: group.variants, variantCount: group.variants.length, score, terms,
    matchKind: direct.length ? 'lexical' : 'fuzzy', ...searchSnippet(doc, terms, corpus)}));
};
